import { HTMLAttributes } from 'react'
import { createContext, useContext } from 'react'

import { cva, type VariantProps } from 'class-variance-authority'

import { cn } from '@/shared/lib/utils'
import {
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
  DialogClose
} from '@/shared/ui/dialog'

import { CardHeader, CardTitle, CardBody, CardFooter } from './Card'
import { CardProvider } from './Card'
import { CardVariant } from './Card'

interface ModalProps {
  children: React.ReactNode
  className?: string
  asCard?: boolean
  variant?: CardVariant
}

type ModalContextType = {
  asCard: boolean
}

interface ModalProviderProps {
  asCard?: boolean
  children: React.ReactNode
}

export const ModalContext = createContext<ModalContextType | undefined>(
  undefined
)

export const useModalContext = () => {
  const context = useContext(ModalContext)
  if (!context) {
    throw new Error('useModalContext must be used within a ModalProvider')
  }
  return context
}

export const ModalProvider = ({
  asCard = false,
  children
}: ModalProviderProps) => {
  return (
    <ModalContext.Provider value={{ asCard }}>{children}</ModalContext.Provider>
  )
}

export const ModalCloseButton = () => {
  return (
    <DialogClose asChild>
      <button
        type="button"
        className="size-10 rounded-full bg-[linear-gradient(0deg,#C91D26.29%,#E0565D_447.79%)] shadow-[0_2px_0px_#6C1B1B,0_4px_4px_rgba(38,3,47,0.25),inset_0_4px_0px_#E24137] flex items-center justify-center absolute top-0 right-0 z-10 translate-x-1/2 -translate-y-1/2 outline-none"
      >
        <svg xmlns="http://www.w3.org/2000/svg" width={20} height={19}>
          <path
            fill="#D9D9D9"
            d="M14.675.665a2.078 2.078 0 0 1 2.873 0l1.366 1.324a1.925 1.925 0 0 1 0 2.783l-4.687 4.541 4.687 4.541a1.925 1.925 0 0 1 0 2.783l-1.366 1.324a2.078 2.078 0 0 1-2.873 0L9.987 13.42 5.3 17.961a2.078 2.078 0 0 1-2.873 0L1.06 16.637a1.925 1.925 0 0 1 0-2.783l4.687-4.541L1.06 4.772a1.925 1.925 0 0 1 0-2.783L2.427.665a2.078 2.078 0 0 1 2.873 0l4.687 4.54 4.688-4.54Z"
          />
        </svg>
      </button>
    </DialogClose>
  )
}

export const modalCardVariants = cva(
  'rounded-xl my-[20px] max-w-2xl w-[90%] px-5 py-0 relative flex flex-col border-0',
  {
    variants: {
      variant: {
        default:
          'bg-[linear-gradient(0deg,#FBFBFB_35.29%,#D5D2FF_447.79%)] shadow-[0_8px_0px_var(--color-primary),0_-4px_0px_var(--color-primary),0_4px_25px_rgba(136,0,134,0.8)]',
        primary:
          'bg-[linear-gradient(0deg,#FBFBFB_35.29%,#D5D2FF_447.79%)] shadow-[0_8px_0px_var(--color-primary),0_-4px_0px_var(--color-primary),0_4px_25px_rgba(136,0,134,0.8)]',
        purple:
          'bg-[linear-gradient(0deg,#FBFBFB_35.29%,#E8D4F4_447.79%)] shadow-[0_8px_0px_#5c1a7a,0_-4px_0px_#5c1a7a,0_4px_25px_rgba(120,34,159,0.5)]',
        yellow:
          'bg-[linear-gradient(0deg,#FBFBFB_35.29%,#F5ECD4_447.79%)] shadow-[0_8px_0px_#F39D72,0_-4px_0px_#F39D72,0_4px_25px_rgba(243,157,114,0.4)]',
        lightBlue:
          'bg-[linear-gradient(0deg,#FBFBFB_35.29%,#D4E8F7_447.79%)] shadow-[0_8px_0px_#5a9fc9,0_-4px_0px_#5a9fc9,0_4px_25px_rgba(90,159,201,0.4)]',
        gray: 'bg-card',
        dark: 'bg-[#1D1D2A] text-white shadow-[0_8px_0px_#000,0_-4px_0px_#182A62,0_4px_25px_rgba(136,0,134,0.8)]'
      },
      asCard: {
        true: '',
        false: ''
      }
    },
    defaultVariants: {
      variant: 'default',
      asCard: false
    }
  }
)

export function Modal({
  children,
  className,
  asCard = false,
  variant = 'default'
}: ModalProps) {
  const cardClassName = modalCardVariants({ variant, asCard, className })

  const defaultClassName = cn(
    'max-w-md w-[90%] mx-auto p-0 bg-white border-0',
    'shadow-[0px_8px_0px_var(--color-primary),_0px_0px_25px_rgba(136,0,134,0.8),inset_0_4px_0px_#FDF5FF]',
    'rounded-3xl p-5 mt-14 md:mt-0 mb-20 md:mb-0'
  )

  if (asCard) {
    return (
      <ModalProvider asCard={asCard}>
        <CardProvider variant={variant}>
          <DialogContent className={cardClassName} hideCloseButton>
            <ModalCloseButton />
            {children}
          </DialogContent>
        </CardProvider>
      </ModalProvider>
    )
  }

  return (
    <ModalProvider asCard={asCard}>
      <DialogContent
        className={cn(defaultClassName, className)}
        hideCloseButton
      >
        <ModalCloseButton />
        {children}
      </DialogContent>
    </ModalProvider>
  )
}

export const ModalHeader = ({
  children,
  className
}: {
  children: React.ReactNode
  className?: string
}) => {
  const { asCard } = useModalContext()

  if (asCard) {
    return <CardHeader className={className}>{children}</CardHeader>
  }

  return <DialogHeader className={className}>{children}</DialogHeader>
}

export const ModalTitle = ({
  children,
  className
}: {
  children: React.ReactNode
  className?: string
}) => {
  const { asCard } = useModalContext()

  if (asCard) {
    return (
      <CardTitle>
        <DialogTitle>{children}</DialogTitle>
      </CardTitle>
    )
  }

  return (
    <DialogTitle
      className={cn(
        'text-xl text-primary-dark font-sans font-semibold',
        className
      )}
    >
      {children}
    </DialogTitle>
  )
}

export const ModalDescription = ({
  children,
  className,
  ...props
}: HTMLAttributes<HTMLDivElement>) => {
  const { asCard } = useModalContext()

  if (asCard) {
    return (
      <CardBody className={className} {...props}>
        <DialogDescription>{children}</DialogDescription>
      </CardBody>
    )
  }

  return (
    <DialogDescription
      className={cn(
        'text-dark leading-relaxed text-sm font-semibold font-[#1D1D2A]',
        className
      )}
      {...props}
    >
      {children}
    </DialogDescription>
  )
}

export const ModalBody = ({
  children,
  className
}: {
  children: React.ReactNode
  className?: string
}) => {
  const { asCard } = useModalContext()

  if (asCard) {
    return <CardBody className={className}>{children}</CardBody>
  }

  return children
}

export const ModalFooter = ({
  children,
  className
}: {
  children: React.ReactNode
  className?: string
}) => {
  const { asCard } = useModalContext()

  if (asCard) {
    return <CardFooter className={className}>{children}</CardFooter>
  }

  return <DialogFooter className={className}>{children}</DialogFooter>
}
